import { DetailShell } from "@/components/DetailShell"
import { Field } from "@/components/Field"
import { Link } from "@/components/Link"
import { Section } from "@/components/Section"
import { client } from "@/lib/client"
import { dash } from "@/lib/utils"
import { useApi, type Nav } from "@/lib/hooks"
import type { CaseDetail as CaseData } from "@shared/types"

const CLUSTER_TEXT = [
  ["syllabus", "Syllabus"],
  ["summary", "Summary"],
  ["proceduralHistory", "Procedural history"],
  ["history", "History"],
  ["disposition", "Disposition"],
  ["posture", "Posture"],
  ["natureOfSuit", "Nature of suit"],
  ["attorneys", "Attorneys"],
  ["headnotes", "Headnotes"],
] as const

function OpinionCard({ op }: { op: CaseData["opinions"][number] }) {
  const body = op.htmlWithCitations || op.html || ""
  const label = [op.type, op.perCuriam ? "(per curiam)" : ""].filter(Boolean).join(" ")
  return (
    <div className="space-y-2 rounded border p-3">
      <div className="text-sm font-medium">
        {dash(label)}
        {op.authorStr ? ` — ${op.authorStr}` : ""}
        {op.pageCount ? ` · ${op.pageCount} pp` : ""}
      </div>
      {op.downloadUrl && (
        <a className="text-sm text-primary hover:underline" href={op.downloadUrl} target="_blank" rel="noreferrer">
          Original document ↗
        </a>
      )}
      {body ? (
        <iframe title={`opinion-${op.id}`} sandbox="" srcDoc={body} className="h-96 w-full rounded border bg-white" />
      ) : op.plainText ? (
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded border p-2 text-sm">{op.plainText}</pre>
      ) : (
        <p className="text-sm text-muted-foreground">No document body.</p>
      )}
    </div>
  )
}

export function CaseDetail({ id, nav }: { id: string; nav: Nav }) {
  const { data, err } = useApi(() => client.api.cases[":id"].$get({ param: { id } }), [id])
  return (
    <DetailShell title={dash(data?.caseName)} backTo="/cases" nav={nav} data={data} err={err}>
      {data && (
        <div className="space-y-6">
          <div className="space-y-1">
            <Field label="Full name" value={dash(data.caseNameFull)} />
            <Field
              label="Court"
              value={
                data.courtId ? (
                  <Link to={`/courts/${data.courtId}`} nav={nav}>
                    {dash(data.courtName)}
                  </Link>
                ) : (
                  dash(data.courtName)
                )
              }
            />
            <Field label="Docket #" value={dash(data.docketNumber)} />
            <Field label="Date filed" value={dash(data.dateFiled)} />
            <Field label="Status" value={dash(data.precedentialStatus)} />
            <Field label="Citations" value={data.citationCount} />
            <Field label="Judges" value={dash(data.judges)} />
          </div>

          {CLUSTER_TEXT.some(([k]) => data[k]) && (
            <div className="space-y-2">
              {CLUSTER_TEXT.filter(([k]) => data[k]).map(([k, label]) => (
                <details key={k} className="rounded border p-2">
                  <summary className="cursor-pointer text-sm font-medium">{label}</summary>
                  <div className="mt-2 whitespace-pre-wrap text-sm">{data[k]}</div>
                </details>
              ))}
            </div>
          )}

          <Section title={`Opinions (${data.opinions.length})`}>
            {data.opinions.length ? (
              <div className="space-y-4">
                {data.opinions.map((op) => (
                  <OpinionCard key={op.id} op={op} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
          </Section>
        </div>
      )}
    </DetailShell>
  )
}
